// Green Door CRM - Main App Component
import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { OnboardingProvider } from './context/OnboardingContext';
import { toast, Toaster } from './components/ui/sonner';
import OfflineBanner from './components/layout/OfflineBanner';

// Pages
import LoginPage from './pages/auth/LoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import TasksPage from './pages/tasks/TasksPage';
import AccountsListPage from './pages/crm/AccountsListPage';
import AccountFormPage from './pages/crm/AccountFormPage';
import AccountDetailPage from './pages/crm/AccountDetailPage';
import ContactsListPage from './pages/crm/ContactsListPage';
import HorsesListPage from './pages/horses/HorsesListPage';
import HorseFormPage from './pages/horses/HorseFormPage';
import HorseDetailPage from './pages/horses/HorseDetailPage';
import FarmFolioGlobalPage from './pages/farmfolio/FarmFolioGlobalPage';
import SaleOpportunitiesPage from './pages/sales/SaleOpportunitiesPage';
import SaleOpportunityFormPage from './pages/sales/SaleOpportunityFormPage';
import SaleOpportunityDetailPage from './pages/sales/SaleOpportunityDetailPage';
import SelectDeliverablesPage from './pages/sales/SelectDeliverablesPage';
import HistoricSalesPage from './pages/sales/HistoricSalesPage';
import ActiveSalesPage from './pages/sales/ActiveSalesPage';
import CardManagementPage from './pages/sales/CardManagementPage';
import CardViewPage from './pages/sales/CardViewPage';
import MultiCardRunnerPage from './pages/sales/MultiCardRunnerPage';
import ViewerSummaryPage from './pages/sales/ViewerSummaryPage';
import VettingProceduresPage from './pages/sales/VettingProceduresPage';
import InvoicesListPage from './pages/invoices/InvoicesListPage';
import InvoiceDetailPage from './pages/invoices/InvoiceDetailPage';
import StandardItemsPage from './pages/invoices/StandardItemsPage';
import StatementsPage from './pages/invoices/StatementsPage';
import StatementDetailPage from './pages/invoices/StatementDetailPage';
import RecurringInvoicesPage from './pages/invoices/RecurringInvoicesPage';
import CalendarPage from './pages/calendar/CalendarPage';
import ReportsPage from './pages/reports/ReportsPage';
import SavedReportDetailPage from './pages/reports/SavedReportDetailPage';
import SystemDashboardPage from './pages/admin/SystemDashboardPage';
import SystemConfigPage from './pages/admin/SystemConfigPage';
import SupportCenterPage from './pages/admin/SupportCenterPage';
import DataImportPage from './pages/admin/DataImportPage';
import ManageSalesPage from './pages/admin/ManageSalesPage';
import MonitoringPage from './pages/admin/MonitoringPage';
import SystemUsersPage from './pages/admin/SystemUsersPage';
import AuditLogPage from './pages/admin/AuditLogPage';
import TenantAdminPage from './pages/tenant/TenantAdminPage';
import UserProfilePage from './pages/user/UserProfilePage';
import OwnerPortalPage from './pages/owner/OwnerPortalPage';
import BuyerHandoverPage from './pages/buyer/BuyerHandoverPage';
import SignAuthorizationPage from './pages/owner/SignAuthorizationPage';
import ContractSignPage from './pages/sales/ContractSignPage';
import KioskPage from './pages/public/KioskPage';
import SignupPage from './pages/public/SignupPage';
import FarmSignupPage from './pages/public/FarmSignupPage';
import MySyndicatesPage from './pages/syndicates/MySyndicatesPage';
import SyndicateDetailPage from './pages/syndicates/SyndicateDetailPage';

// Components
import AIChatbot from './components/chat/AIChatbot';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user?.roles) {
    const hasAccess = allowedRoles.some((role) => user.roles.includes(role));
    if (!hasAccess) {
      // Redirect restricted roles to sales, others to dashboard
      if (user?.roles?.some(r => ['tenant_card_runner', 'tenant_sale_clerk'].includes(r))) {
        return <Navigate to="/sales" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Public Route (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect based on role
    if (user?.roles?.includes('system_owner')) {
      return <Navigate to="/admin/system" replace />;
    } else if (user?.roles?.includes('owner')) {
      return <Navigate to="/owner-portal" replace />;
    } else if (user?.roles?.some(r => ['tenant_card_runner', 'tenant_sale_clerk'].includes(r))) {
      return <Navigate to="/sales" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/kiosk/:saleId/:token" element={<KioskPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/signup/farm" element={<FarmSignupPage />} />

      {/* Tenant User Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tasks"
        element={
          <ProtectedRoute>
            <TasksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/accounts"
        element={
          <ProtectedRoute>
            <AccountsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/accounts/new"
        element={
          <ProtectedRoute>
            <AccountFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/accounts/:id"
        element={
          <ProtectedRoute>
            <AccountDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/accounts/:id/edit"
        element={
          <ProtectedRoute>
            <AccountFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/contacts"
        element={
          <ProtectedRoute>
            <ContactsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/contacts/new"
        element={
          <ProtectedRoute>
            <AccountFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/horses"
        element={
          <ProtectedRoute>
            <HorsesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/horses/new"
        element={
          <ProtectedRoute>
            <HorseFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/horses/:id"
        element={
          <ProtectedRoute>
            <HorseDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/horses/:id/edit"
        element={
          <ProtectedRoute>
            <HorseFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/farmfolio"
        element={
          <ProtectedRoute>
            <FarmFolioGlobalPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/opportunities"
        element={
          <ProtectedRoute>
            <SaleOpportunitiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/opportunities/historic"
        element={
          <ProtectedRoute>
            <HistoricSalesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/opportunities/new"
        element={
          <ProtectedRoute>
            <SaleOpportunityFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/opportunities/:id"
        element={
          <ProtectedRoute>
            <SaleOpportunityDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/opportunities/:id/select-deliverables"
        element={
          <ProtectedRoute>
            <SelectDeliverablesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/:saleId/card-management"
        element={
          <ProtectedRoute>
            <CardManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/multi-card-runner"
        element={
          <ProtectedRoute>
            <MultiCardRunnerPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/:saleId/cards/:cardId"
        element={
          <ProtectedRoute>
            <CardViewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/:saleId/viewer-summary"
        element={
          <ProtectedRoute>
            <ViewerSummaryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/:saleId/vetting-procedures"
        element={
          <ProtectedRoute>
            <VettingProceduresPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <ActiveSalesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <InvoicesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/standard-items"
        element={
          <ProtectedRoute>
            <StandardItemsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/statements"
        element={
          <ProtectedRoute>
            <StatementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/statements/:statementId"
        element={
          <ProtectedRoute>
            <StatementDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/recurring"
        element={
          <ProtectedRoute>
            <RecurringInvoicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:invoiceId"
        element={
          <ProtectedRoute>
            <InvoiceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/saved/:reportId"
        element={
          <ProtectedRoute>
            <SavedReportDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tenant-admin"
        element={
          <ProtectedRoute allowedRoles={['tenant_system_admin']}>
            <TenantAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <UserProfilePage />
          </ProtectedRoute>
        }
      />

      {/* System Owner Routes */}
      <Route
        path="/admin/system"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <SystemDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/config"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <SystemConfigPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <SupportCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/data-import"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <DataImportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sales"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <ManageSalesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/monitoring"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <MonitoringPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system-users"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <SystemUsersPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/audit"
        element={
          <ProtectedRoute allowedRoles={['system_owner']}>
            <AuditLogPage />
          </ProtectedRoute>
        }
      />

      {/* Syndicator Routes */}
      <Route
        path="/syndicates"
        element={
          <ProtectedRoute>
            <MySyndicatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/syndicates/:id"
        element={
          <ProtectedRoute>
            <SyndicateDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Owner Portal Routes */}
      <Route
        path="/owner-portal"
        element={
          <ProtectedRoute allowedRoles={['owner']}>
            <OwnerPortalPage />
          </ProtectedRoute>
        }
      />
      <Route path="/handover/:token" element={<BuyerHandoverPage />} />
      <Route path="/sign-authorization/:id" element={
        <ProtectedRoute allowedRoles={['owner', 'tenant_user', 'tenant_system_admin']}>
          <SignAuthorizationPage />
        </ProtectedRoute>
      } />
      <Route path="/contract-sign/:id" element={
        <ProtectedRoute allowedRoles={['tenant_user', 'tenant_system_admin']}>
          <ContractSignPage />
        </ProtectedRoute>
      } />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  const toastShownRef = useRef(false);

  // Show a non-intrusive update prompt when the Service Worker detects a new version
  useEffect(() => {
    const handleSwUpdate = () => {
      if (toastShownRef.current) return; // only show once per session
      toastShownRef.current = true;
      toast('A new version is available', {
        description: 'Refresh to get the latest updates.',
        duration: Infinity,
        action: {
          label: 'Refresh now',
          onClick: () => window.location.reload(),
        },
      });
    };
    window.addEventListener('sw-update-available', handleSwUpdate);
    return () => window.removeEventListener('sw-update-available', handleSwUpdate);
  }, []);

  // Global date input handler
  useEffect(() => {
    // Add event listeners to all date inputs
    const handleDateInputs = () => {
      document.querySelectorAll('input[type="date"]').forEach(input => {
        // Set initial state
        if (input.value) {
          input.classList.add('date-filled');
        } else {
          input.classList.remove('date-filled');
        }
        
        // Add change listener if not already added
        if (!input.dataset.dateListenerAdded) {
          input.addEventListener('change', (e) => {
            if (e.target.value) {
              e.target.classList.add('date-filled');
            } else {
              e.target.classList.remove('date-filled');
            }
          });
          input.dataset.dateListenerAdded = 'true';
        }
      });
    };
    
    // Run initially
    handleDateInputs();
    
    // Run whenever DOM changes (for dynamically added inputs)
    const observer = new MutationObserver(handleDateInputs);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <OnboardingProvider>
          <OfflineBanner />
          <AppRoutes />
          <ChatbotWrapper />
          <Toaster position="top-right" richColors />
        </OnboardingProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Chatbot wrapper that only shows when authenticated
function ChatbotWrapper() {
  const { isAuthenticated, isOwner } = useAuth();
  
  // Show chatbot for authenticated tenant users, not for owners (they have simpler portal)
  if (!isAuthenticated || isOwner()) {
    return null;
  }
  
  return <AIChatbot />;
}

export default App;
